import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATHS } from 'routes/constants';
import { saveUrl } from 'redux/reducers/auth/reducer';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { getPpwrFeeOptionsRequest, searchPpwrEmSettingsRequest } from 'redux/reducers/ppwr/reducer';
import {
	getPpwrEmSettings,
	getPpwrEmSettingsLoading,
	getPpwrFeeOptions,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrEmSettings } from 'redux/reducers/ppwr/types';
import { tableStructure as defaultTableStructure } from './tableStructure';

export const Ppwr: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const list = useSelector(getPpwrEmSettings);
	const listLoading = useSelector(getPpwrEmSettingsLoading);
	const feeOptions = useSelector(getPpwrFeeOptions);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TPpwrEmSettings>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchPpwrEmSettingsRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	/** Plan names come from the EPR service fees list (POST /api/ppwr_fees/search). */
	useEffect(() => {
		if (!feeOptions) dispatch(getPpwrFeeOptionsRequest());
	}, [dispatch, feeOptions]);

	const tableStructure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.selector === 'subscriptionPlanId'
					? {
							...column,
							value: (item: TPpwrEmSettings) => {
								if (item.subscriptionPlanId == null) return '';
								const fee = feeOptions?.find(({ id }) => id === item.subscriptionPlanId);
								if (!fee) return String(item.subscriptionPlanId);
								return fee.planName ?? `Plan #${fee.id}`;
							},
						}
					: column,
			),
		[currentTableStructure, feeOptions],
	);

	const handleAction = useCallback(() => {}, []);

	const handleLink = useCallback(
		(emId: number) => {
			dispatch(saveUrl(window.location.pathname + window.location.search));
			navigate(PATHS.PPWR_SETTINGS_PAGE.replace(':emId', String(emId)));
		},
		[dispatch, navigate],
	);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TPpwrEmSettings>
				tableStructure={tableStructure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				handleLink={handleLink}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Extra Services / <b>EPR/PPWR</b>
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => navigate(PATHS.PPWR_REGISTRATION_FORMS_PAGE)}
					>
						Add New EPR/PPWR
					</Button>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
